import { useState, useEffect, useCallback, memo } from 'react'
import { Search, Edit2, Check, Download, AlertTriangle, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getVehicles, updateVehicle, createVehicle } from '../services/vehicleService.js'
import { useToast } from '../components/Toast.jsx'
import Pagination from '../components/Pagination.jsx'
import Modal from '../components/Modal.jsx'
import FormField from '../components/FormField.jsx'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import { statusColors, STATUS_OPTIONS, FUEL_OPTIONS, DEFAULT_PAGE_SIZE } from '../constants/statusColors.js'
import { validateVehicleForm, validateCreateVehicleForm, hasErrors } from '../utils/validation.js'
import { exportToCsv } from '../utils/csv.js'

const inputCls = 'w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'

const VehicleRow = memo(({ v, onEdit }) => (
  <tr className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
    <td className="px-6 py-4">
      <Link to={`/vehicles/${v.vehicleUuid}`} className="font-mono font-semibold text-sm text-blue-400 hover:text-blue-300 transition-colors">{v.plate}</Link>
      <span className="text-xs text-gray-400 ml-1">{v.plateCountry}</span>
    </td>
    <td className="px-6 py-4 font-mono text-xs text-gray-400">{v.vin || '-'}</td>
    <td className="px-6 py-4 text-sm">{[v.brandName, v.modelName].filter(Boolean).join(' ') || '-'}</td>
    <td className="px-6 py-4 text-center text-sm">{v.productionYear || '-'}</td>
    <td className="px-6 py-4 text-center text-xs">{v.fuelType || '-'}</td>
    <td className="px-6 py-4 text-center">
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[v.status] || 'bg-gray-500/20 text-gray-400'}`}>
        {v.status}
      </span>
    </td>
    <td className="px-6 py-4 text-right text-sm">{v.mileage != null ? v.mileage.toLocaleString() + ' km' : '-'}</td>
    <td className="px-6 py-4 text-right">
      <button onClick={() => onEdit(v)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" aria-label={`Edit vehicle ${v.plate}`}>
        <Edit2 className="w-4 h-4 text-gray-400" />
      </button>
    </td>
  </tr>
))
VehicleRow.displayName = 'VehicleRow'

const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({ status: '', fuelType: '', search: '' })
  const [searchInput, setSearchInput] = useState('')
  const [editVehicle, setEditVehicle] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [formErrors, setFormErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [confirmClose, setConfirmClose] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const [confirmStatus, setConfirmStatus] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState({ plate: '', plateCountry: 'GB', vin: '' })
  const [createErrors, setCreateErrors] = useState({})
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState(null)
  const toast = useToast()

  const fetchVehicles = useCallback(() => {
    setLoading(true)
    setError(null)
    getVehicles(page, DEFAULT_PAGE_SIZE, filters)
      .then((res) => {
        const data = res.data
        setVehicles(data.content || [])
        setTotalPages(data.totalPages || 0)
        setTotalElements(data.totalElements || 0)
      })
      .catch(() => setError('Failed to load vehicles'))
      .finally(() => setLoading(false))
  }, [page, filters])

  useEffect(() => { fetchVehicles() }, [fetchVehicles])

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchInput }))
      setPage(0)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const openEdit = useCallback((v) => {
    setEditVehicle(v)
    setEditForm({
      plate: v.plate || '',
      status: v.status || 'ACTIVE',
      mileage: v.mileage ?? '',
      notes: v.notes || '',
      insuranceExpiryDate: v.insuranceExpiryDate || '',
      technicalInspectionExpiryDate: v.technicalInspectionExpiryDate || '',
      taxDueDate: v.taxDueDate || '',
    })
    setFormErrors({})
    setSaveError(null)
    setFormDirty(false)
  }, [])

  const handleCloseModal = useCallback(() => {
    if (formDirty) {
      setConfirmClose(true)
    } else {
      setEditVehicle(null)
    }
  }, [formDirty])

  const updateField = useCallback((field, value) => {
    setEditForm(f => ({ ...f, [field]: value }))
    setFormDirty(true)
    setFormErrors(prev => ({ ...prev, [field]: undefined }))
  }, [])

  const handleStatusChange = useCallback((newStatus) => {
    if (newStatus === 'SCRAPPED' || newStatus === 'STOLEN') {
      setConfirmStatus(newStatus)
    } else {
      updateField('status', newStatus)
    }
  }, [updateField])

  const saveEdit = async () => {
    const errors = validateVehicleForm(editForm)
    if (hasErrors(errors)) {
      setFormErrors(errors)
      return
    }
    if (!editVehicle) return
    setSaving(true)
    setSaveError(null)
    try {
      const data = { ...editForm }
      if (data.mileage === '') delete data.mileage
      else data.mileage = Number(data.mileage)
      if (!data.insuranceExpiryDate) delete data.insuranceExpiryDate
      if (!data.technicalInspectionExpiryDate) delete data.technicalInspectionExpiryDate
      if (!data.taxDueDate) delete data.taxDueDate
      await updateVehicle(editVehicle.vehicleUuid, data)
      setEditVehicle(null)
      toast.success('Vehicle updated successfully')
      fetchVehicles()
    } catch {
      setSaveError('Failed to update vehicle. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleExport = useCallback(() => {
    const headers = ['Plate', 'Country', 'VIN', 'Brand', 'Model', 'Year', 'Fuel', 'Status', 'Mileage']
    const rows = vehicles.map(v => [v.plate, v.plateCountry, v.vin || '', v.brandName || '', v.modelName || '', v.productionYear || '', v.fuelType || '', v.status, v.mileage ?? ''])
    exportToCsv('vehicles.csv', headers, rows)
    toast.success(`Exported ${vehicles.length} vehicles to CSV`)
  }, [vehicles, toast])

  const handleCreate = async () => {
    const errors = validateCreateVehicleForm(createForm)
    if (hasErrors(errors)) { setCreateErrors(errors); return }
    setCreating(true)
    setCreateError(null)
    try {
      await createVehicle(createForm)
      setCreateOpen(false)
      setCreateForm({ plate: '', plateCountry: 'GB', vin: '' })
      toast.success('Vehicle created successfully')
      fetchVehicles()
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create vehicle')
    } finally {
      setCreating(false)
    }
  }

  const hasFilters = filters.search || filters.status || filters.fuelType
  const emptyMessage = hasFilters
    ? `No vehicles match current filters`
    : 'No vehicles found'

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
          <input type="text" placeholder="Search by plate or VIN..."
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            aria-label="Search vehicles" />
        </div>
        <select value={filters.status} onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(0) }}
          aria-label="Filter by status"
          className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.fuelType} onChange={(e) => { setFilters(f => ({ ...f, fuelType: e.target.value })); setPage(0) }}
          aria-label="Filter by fuel type"
          className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Fuel</option>
          {FUEL_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <button onClick={() => { setCreateOpen(true); setCreateForm({ plate: '', plateCountry: 'GB', vin: '' }); setCreateErrors({}); setCreateError(null) }}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
          aria-label="Create new vehicle">
          <Plus className="w-4 h-4" /> New Vehicle
        </button>
        <button onClick={handleExport} disabled={vehicles.length === 0}
          className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-30"
          aria-label="Export vehicles to CSV">
          <Download className="w-4 h-4" /> Export
        </button>
        <span className="text-sm text-gray-400">{totalElements} vehicles</span>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
          <button onClick={fetchVehicles} className="ml-auto underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Plate</th>
              <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">VIN</th>
              <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Brand / Model</th>
              <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Year</th>
              <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Fuel</th>
              <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
              <th scope="col" className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Mileage</th>
              <th scope="col" className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
            ) : vehicles.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400">{emptyMessage}</td></tr>
            ) : (
              vehicles.map((v) => <VehicleRow key={v.vehicleUuid} v={v} onEdit={openEdit} />)
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={setPage} label="vehicles" />

      {/* Edit Modal */}
      <Modal open={!!editVehicle} title="Edit Vehicle" onClose={handleCloseModal} maxWidth="max-w-lg" footer={
        <>
          <button onClick={handleCloseModal} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={saveEdit} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <Check className="w-4 h-4" />{saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }>
        {saveError && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{saveError}</div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Plate" htmlFor="edit-v-plate" error={formErrors.plate}>
            <input id="edit-v-plate" type="text" value={editForm.plate || ''} onChange={(e) => updateField('plate', e.target.value)} className={inputCls} />
          </FormField>
          <FormField label="Status" htmlFor="edit-v-status">
            <select id="edit-v-status" value={editForm.status || ''} onChange={(e) => handleStatusChange(e.target.value)} className={inputCls}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FormField>
        </div>
        <FormField label="Mileage (km)" htmlFor="edit-v-mileage" error={formErrors.mileage}>
          <input id="edit-v-mileage" type="number" min="0" value={editForm.mileage ?? ''} onChange={(e) => updateField('mileage', e.target.value)} className={inputCls} />
        </FormField>
        <FormField label="Notes" htmlFor="edit-v-notes">
          <textarea id="edit-v-notes" value={editForm.notes || ''} onChange={(e) => updateField('notes', e.target.value)} rows={3} className={`${inputCls} resize-none`} />
        </FormField>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormField label="Insurance Expiry" htmlFor="edit-v-insurance">
            <input id="edit-v-insurance" type="date" value={editForm.insuranceExpiryDate || ''} onChange={(e) => updateField('insuranceExpiryDate', e.target.value)} className={inputCls} />
          </FormField>
          <FormField label="MOT Expiry" htmlFor="edit-v-mot">
            <input id="edit-v-mot" type="date" value={editForm.technicalInspectionExpiryDate || ''} onChange={(e) => updateField('technicalInspectionExpiryDate', e.target.value)} className={inputCls} />
          </FormField>
          <FormField label="Tax Due Date" htmlFor="edit-v-tax">
            <input id="edit-v-tax" type="date" value={editForm.taxDueDate || ''} onChange={(e) => updateField('taxDueDate', e.target.value)} className={inputCls} />
          </FormField>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmClose}
        title="Unsaved changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmLabel="Discard"
        onConfirm={() => { setConfirmClose(false); setEditVehicle(null) }}
        onCancel={() => setConfirmClose(false)}
      />

      <ConfirmDialog
        open={!!confirmStatus}
        title={`Mark vehicle as ${confirmStatus}?`}
        message={`This will mark the vehicle as ${confirmStatus}. This is a significant status change.`}
        confirmLabel={`Yes, mark as ${confirmStatus}`}
        variant="danger"
        onConfirm={() => { updateField('status', confirmStatus); setConfirmStatus(null) }}
        onCancel={() => setConfirmStatus(null)}
      />

      {/* Create Vehicle Modal */}
      <Modal open={createOpen} title="Create Vehicle" onClose={() => setCreateOpen(false)} maxWidth="max-w-md" footer={
        <>
          <button onClick={() => setCreateOpen(false)} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={handleCreate} disabled={creating}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <Plus className="w-4 h-4" />{creating ? 'Creating...' : 'Create'}
          </button>
        </>
      }>
        {createError && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{createError}</div>}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Plate" htmlFor="create-v-plate" error={createErrors.plate}>
            <input id="create-v-plate" type="text" placeholder="e.g. CA1234AB" value={createForm.plate}
              onChange={(e) => { setCreateForm(f => ({ ...f, plate: e.target.value.toUpperCase() })); setCreateErrors(e2 => ({ ...e2, plate: undefined })) }}
              className={inputCls} />
          </FormField>
          <FormField label="Country" htmlFor="create-v-country" error={createErrors.plateCountry}>
            <input id="create-v-country" type="text" placeholder="GB" value={createForm.plateCountry}
              onChange={(e) => { setCreateForm(f => ({ ...f, plateCountry: e.target.value.toUpperCase() })); setCreateErrors(e2 => ({ ...e2, plateCountry: undefined })) }}
              className={inputCls} maxLength={3} />
          </FormField>
        </div>
        <FormField label="VIN (optional)" htmlFor="create-v-vin" error={createErrors.vin}>
          <input id="create-v-vin" type="text" placeholder="17-character VIN" value={createForm.vin}
            onChange={(e) => { setCreateForm(f => ({ ...f, vin: e.target.value.toUpperCase() })); setCreateErrors(e2 => ({ ...e2, vin: undefined })) }}
            className={inputCls} maxLength={17} />
        </FormField>
      </Modal>
    </div>
  )
}

export default VehiclesPage
